// src/components/communications/ConversationList.jsx
// Left panel - Client conversation list with filters and search
import React from 'react';
import {
  MessageSquare, Mail, Phone, Search, Inbox, ChevronRight,
  Loader2
} from 'lucide-react';

// Filter tabs configuration
const FILTER_TABS = [
  { id: 'all', label: 'All', icon: Inbox, badgeKey: null },
  { id: 'unread', label: 'Unread', icon: Mail, badgeKey: 'total' },
  { id: 'sms', label: 'SMS', icon: MessageSquare, badgeKey: 'sms' },
  { id: 'email', label: 'Email', icon: Mail, badgeKey: 'email' },
  { id: 'call', label: 'Calls', icon: Phone, badgeKey: 'call' },
];

// Get type icon component
const getTypeIcon = (type) => {
  switch (type) {
    case 'sms': return MessageSquare;
    case 'email': return Mail;
    case 'call': return Phone;
    default: return MessageSquare;
  }
};

// Format relative time
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
};

export default function ConversationList({
  conversations,
  selectedClientId,
  onSelect,
  activeFilter,
  onFilterChange,
  searchQuery,
  onSearchChange,
  unreadCounts,
  loading,
  isMobile
}) {
  return (
    <div className={`${isMobile ? 'w-full' : 'w-80'} flex-shrink-0 bg-white border-r border-gray-200 flex flex-col h-full`}>
      {/* Filter Tabs */}
      <div className="flex items-center gap-1 p-2 border-b border-gray-200 overflow-x-auto">
        {FILTER_TABS.map((tab) => {
          const Icon = tab.icon;
          const count = tab.badgeKey ? unreadCounts[tab.badgeKey] : null;
          const isActive = activeFilter === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => onFilterChange(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
              {count > 0 && (
                <span className={`min-w-[18px] h-[18px] text-xs rounded-full flex items-center justify-center ${
                  isActive ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="p-3 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-6 text-center text-gray-400">
            <Inbox className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No conversations</p>
            <p className="text-xs mt-1">
              {activeFilter !== 'all'
                ? 'Try changing your filter'
                : searchQuery
                  ? 'No results match your search'
                  : 'No conversations yet'}
            </p>
          </div>
        ) : (
          conversations.map((conversation) => {
            const isSelected = selectedClientId === conversation.clientId;
            const hasUnread = conversation.unreadCount > 0;
            const TypeIcon = getTypeIcon(conversation.lastMessage?.type);
            const initials = getInitials(conversation.clientName);

            return (
              <div
                key={conversation.clientId}
                onClick={() => onSelect(conversation.clientId)}
                className={`
                  flex items-center gap-3 p-3 cursor-pointer transition-all border-l-4
                  ${isSelected
                    ? 'bg-amber-50 border-amber-500'
                    : hasUnread
                      ? 'bg-blue-50/30 border-transparent hover:bg-blue-50/50'
                      : 'border-transparent hover:bg-gray-50'
                  }
                `}
              >
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {initials}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`font-medium truncate ${hasUnread ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
                      {conversation.clientName || 'Unknown'}
                    </span>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {formatRelativeTime(conversation.lastMessage?.timestamp || conversation.lastMessageTime)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <TypeIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span className={`text-sm truncate ${hasUnread ? 'text-gray-700' : 'text-gray-500'}`}>
                      {conversation.lastMessage?.direction === 'outbound' && (
                        <span className="text-gray-400">You: </span>
                      )}
                      {conversation.lastMessage?.body || conversation.lastMessage?.subject || 'No message'}
                    </span>
                  </div>
                </div>

                {/* Unread Badge */}
                {hasUnread && (
                  <span className="min-w-[20px] h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-bold px-1.5 flex-shrink-0">
                    {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                  </span>
                )}

                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
